import CandidateComponent from '@/public/vote/components/candidate-component/candidate-component.vue';
import CandidateSearchComponent from '@/public/vote/components/candidate-search-component/candidate-search-component.vue';
import MinMaxValidationHintComponent from '@/public/vote/components/min-max-validation-hint-component/min-max-validation-hint-component.vue';
import { ApiClient } from '@/shared/api-client';
import { ICandidate } from '@/shared/api-client/models/candidate';

import { CandidateProfileModalOpener } from '@/public/services/candidate-profile-modal-opener.service';
import { CandidateMapper, ICandidateBucket } from '@/public/vote/mappers/candidate-mapper';
import { ICandidateViewModel } from '@/public/vote/models/candidate-view-model';
import { ICandidatureElectionVote } from '@/public/vote/models/candidature-election-vote';
import { IElectionInitializationReceipt } from '@/public/vote/models/election-initialization-receipt';
import { IVoteValidationSupport } from '@/public/vote/models/vote-validation-support';
import { IVoteValidationError } from '@/public/vote/services/vote-validation-service';
import { CandidatesVerificationHashCreator, ComplexIdHelper } from '@/shared/helpers';
import { ArgumentValidator } from '@/shared/helpers/argument-validator';
import { ConsoleFacade } from '@/shared/helpers/console-facade';
import { IElection } from '@/shared/models/election';
import _ from 'lodash';
import { Component, Emit, Inject, Prop, Vue } from 'vue-property-decorator';

import { VotingType } from '@/shared/models/voting-type';
import { ICandidateOrderingNewPositionEvent } from '../../candidate-ranking-component/candidate-ranking-component';
import CandidateRankingComponent from '../../candidate-ranking-component/candidate-ranking-component.vue';
import { IVotingSettings } from '@/shared/models/voting-settings';
import { VoteEnvironmentStore } from '@/public/vote/services/vote-environment-store';
import { IPreparedAdditionalData } from '@/public/vote/models/prepared-additional-data';
import ElectionCandidateFilterComponent from '../../../../shared/components/election-candidate-filter-component/election-candidate-filter-component.vue';
import { ICandidateFilterResult } from '../../../../shared/components/election-candidate-filter-component/election-candidate-filter-component';
import { StringComparer } from '@/shared/helpers/string-comparer';

interface IViewModel {

    candidates: ICandidateBucket;

    candidatesVerificationHash: string;

    isLoaded: boolean;

    isStoring: boolean;

    filter?: ICandidateFilterResult;
}

type VoteMode = 'Selection' | 'ElectionCommittee' | 'Blank';

@Component({
    components: {
        CandidateComponent,
        CandidateSearchComponent,
        MinMaxValidationHintComponent,
        CandidateRankingComponent,
        ElectionCandidateFilterComponent
    }
})
export default class CandidacyElectionComponent extends Vue {

    @Inject('voteEnvironment') readonly voteEnvironment!: VoteEnvironmentStore;

    public get selected(): string[] {

        return this.selectedCandidatures.map((c: ICandidate) => c.id);
    }

    public get selectedCandidates(): ICandidate[] {

        return this.selectedCandidatures;
    }

    public get candidates(): ICandidateBucket {

        if (!this.viewModel.candidates) {
            return {};
        }

        return this.viewModel.candidates;
    }

    public get filterResultedInNoHits(): boolean {

        const isFiltered = this.viewModel.filter !== undefined &&
            ((this.viewModel.filter.query !== '' || this.viewModel.filter.aggregations.length > 0));

        return isFiltered && this.otherCandidates.length <= 0;
    }

    private applyCandidateFilter(candidates: ICandidateViewModel[]): ICandidateViewModel[] {

        if (!this.viewModel.filter) {
            return candidates;
        }

        let filteredCandidates = _.clone(candidates);

        if (this.viewModel.filter.query && this.viewModel.filter.query.length > 0) {
            const query = this.viewModel.filter.query ?? '';

            filteredCandidates = filteredCandidates.filter(x => {

                // Search display name
                if (x.object?.displayName.search(new RegExp(query, 'gi')) !== -1) {
                    return true;
                }

                // Search all filterable values
                for(const key in x.object?.filterableProperties) {
                    if (x.object?.filterableProperties[key] && x.object?.filterableProperties[key].search(new RegExp(query, 'gi')) !== -1) {
                        return true;
                    }
                }

                return false;
            });
        }

        if (this.viewModel.filter.aggregations) {

            this.viewModel.filter.aggregations.forEach(f => {

                const propertyName = f.propertyName.toLowerCase();

                filteredCandidates = filteredCandidates.filter(x =>
                    x.object?.filterableProperties &&
                    x.object.filterableProperties[propertyName] &&
                    f.values.findIndex(y => StringComparer.compare(y, x.object.filterableProperties ? x.object.filterableProperties[propertyName] : '')) !== -1
                );
            });
        }

        return filteredCandidates;
    }

    public get candidateFilterEnabled(): boolean {
        return this.votingSettings.candidateFilter &&
            (
                this.votingSettings.candidateFilter.enableFullTextSearch ||
                (this.votingSettings.candidateFilter.filters && this.votingSettings.candidateFilter.filters.length > 0 )
            );
    }

    public get otherCandidates(): ICandidateViewModel[] {

        if (!this.viewModel.candidates) {
            return [];
        }

        if (!this.viewModel.candidates.otherCandidates) {
            return [];
        }

        return this.applyCandidateFilter(_.filter(this.viewModel.candidates.otherCandidates, x => x.visible));
    }

    public get allCandidates(): ICandidate[] {

        const candidates: ICandidateViewModel[] = [];

        if (!this.viewModel.candidates) {
            return [];
        }

        if (this.viewModel.candidates.candidatesNominatedByCommittee) {

            this.viewModel.candidates.candidatesNominatedByCommittee.forEach(x => {
                candidates.push(x);
            })
        }

        if (this.viewModel.candidates.otherCandidates) {

            this.viewModel.candidates.otherCandidates.forEach(x => {
                candidates.push(x);
            })
        }

        return candidates.map(x => x.object);
    }

    public get totalNbrOfCandidates(): number {

        if (!this.viewModel.candidates) {
            return 0;
        }

        const nbrOfCandidates = this.viewModel.candidates.candidatesNominatedByCommittee ? this.viewModel.candidates.candidatesNominatedByCommittee.length : 0;
        const nbrOfOtherCandidates = this.viewModel.candidates.otherCandidates ? this.viewModel.candidates.otherCandidates.length : 0;

        return nbrOfCandidates + nbrOfOtherCandidates;
    }

    public get hiddenCandidates(): ICandidateViewModel[] {

        if (!this.viewModel.candidates) {
            return [];
        }

        if (!this.viewModel.candidates.otherCandidates) {
            return [];
        }

        return _.filter(this.viewModel.candidates.otherCandidates, x => !x.visible);
    }

    public get showCandidatureTypeAhead(): boolean {

        if (this.viewModel.candidates && !!this.hiddenCandidates.length) {

            return true;
        }

        return this.election.candidatureDisplayType === 'TypeAhead';
    }

    public get showGenders(): boolean {

        return this.election.showGenderForUserCandidates !== undefined && this.election.showGenderForUserCandidates;
    }

    public get hasCandidatesNominatedByCommittee(): boolean {

        if (this.candidates.candidatesNominatedByCommittee &&
            this.candidates.candidatesNominatedByCommittee.length > 0) {

            return true;
        }

        if (this.candidates.otherCandidates &&
            this.candidates.otherCandidates.some(x => x.object.nominatedByCommittee === true)) {

            return true;
        }

        return false;
    }

    public get votingType(): VotingType {

        if (!this.election || this.election.electionType !== 'Candidacy') {
            return 'Undefined';
        }

        return this.election.votingType;
    }

    @Prop() public election!: IElection;
    @Prop() public votingSettings!: IVotingSettings;
    @Prop() public translations!: { [key: string]: string; };
    @Prop({default: true}) public showProfilePictures!: boolean;
    @Prop() public allValidationErrors!: IVoteValidationError[];
    @Prop({default: null}) public preparedAdditionalData!: IPreparedAdditionalData[];

    public voteMode: VoteMode = 'Selection';

    public viewModel: IViewModel = {
        candidates: {
            otherCandidates: [],
            candidatesNominatedByCommittee: []
        },
        candidatesVerificationHash: '',
        isLoaded: false,
        isStoring: false,
        filter: undefined
    };

    private apiClient: ApiClient = new ApiClient();

    private selectedCandidatures: ICandidate[] = [];

    public get disableElectionBlankVote(): boolean  {

        const hasParentCollection = this.election && this.election.electionCollection !== null;

        if (hasParentCollection) {

            if (this.election.electionCollection && this.election.electionCollection.enableBlankVoteForAllElections === true) {

                return true;
            }
        }

        return !hasParentCollection;
    }

    public get electionsWithErrors(): string[] {

        if (this.allValidationErrors.length <= 0) {
            return [];
        }

        return _.uniq(this.allValidationErrors.map(x => x.electionId.id));
    }

    public get validationErrors(): string[] {

        const errors: string[] = [];

        this.allValidationErrors.forEach(x => {

            if (x.electionId.id.toLowerCase() !== this.election.electionId.id.toLowerCase()) {
                return;
            }

            const typeAsString = _.camelCase(x.type);

            const errorMessage = this.translations[`validation_message_${typeAsString}`];

            errors.push(errorMessage || typeAsString);
        });

        return errors;
    }

    @Emit('changed')
    public emitVoteChanged(votes: ICandidatureElectionVote): ICandidatureElectionVote {

        return votes;
    }

    @Emit('initiated')
    public emitInitiated(receipt: IElectionInitializationReceipt): IElectionInitializationReceipt {

        return receipt;
    }

    public isSelected(candidatureId: string): boolean {

        return _.includes(this.selected, candidatureId);
    }

    public async filterChanged(filter: ICandidateFilterResult): Promise<void> {

        ArgumentValidator.notNullOrEmpty(filter, `filter`);

        await this.$nextTick();

        this.viewModel.filter = filter;
    }

    public async addCandidate(candidate: ICandidateViewModel) {

        ArgumentValidator.notNullOrEmpty(candidate, `candidate`);

        candidate.visible = true;

        await this.$nextTick();

        this.toggleSelection(candidate);
    }

    public async removeCandidate(candidate: ICandidateViewModel) {

        ArgumentValidator.notNullOrEmpty(candidate, `candidate`);

        candidate.visible = false;

        await this.$nextTick();

        this.ensureVoteRemoved(candidate);
    }

    public async openCandidateProfileModal(candidate: ICandidate) {

        ArgumentValidator.notNullOrEmpty(candidate, `candidate`);

        const containerElement = this.$refs.container as Element;

        await CandidateProfileModalOpener.openAsync(
            containerElement,
            this.election.electionId,
            this.showGenders,
            candidate,
            this.translations,
            this.voteEnvironment?.preventViewChangingLinks ?? false
        );
    }

    public candidateClickedOrPressed(candidate: ICandidateViewModel, $event: UIEvent) {

        $event.preventDefault();

        this.voteMode = 'Selection';

        this.toggleSelection(candidate);
    }

    public toogleElectionCommitteeModeClickedOrPressed($event?: UIEvent): void {

        if ($event) {
            $event.preventDefault();
        }

        this.voteMode = this.voteMode !== 'ElectionCommittee' ? 'ElectionCommittee' : 'Selection';

        let newCandidates: ICandidate[] = [];

        if (this.voteMode === 'ElectionCommittee') {

            if (this.viewModel.candidates.candidatesNominatedByCommittee && this.viewModel.candidates.candidatesNominatedByCommittee.length > 0) {

                newCandidates = newCandidates.concat(
                    this.viewModel.candidates.candidatesNominatedByCommittee
                        .filter(x => !x.object.disabledByNoSelfVoting)
                        .map(x => x.object)
                );
            }

            if (this.viewModel.candidates.otherCandidates && this.viewModel.candidates.otherCandidates.length > 0) {

                newCandidates = newCandidates.concat(
                    this.viewModel.candidates.otherCandidates
                        .filter(x => x.object.nominatedByCommittee === true && !x.object.disabledByNoSelfVoting)
                        .map(x => x.object)
                );
            }
        }

        this.selectedCandidatures = newCandidates;

        this.emitVoteChanged({
            electionId: this.election.electionId,
            candidatesVerificationHash: this.viewModel.candidatesVerificationHash,
            candidatures: newCandidates,
            isBlankVote: false,
            validationSupport: this.setupValidationSupport()
        });
    }

    public async toggleBlankVoteClickedOrPressed($event: UIEvent): Promise<void> {

        $event.preventDefault();

        this.voteMode = this.voteMode !== 'Blank' ? 'Blank' : 'Selection';

        this.selectedCandidatures = [];

        this.emitVoteChanged({
            electionId: this.election.electionId,
            candidatesVerificationHash: this.viewModel.candidatesVerificationHash,
            candidatures: [],
            isBlankVote: this.voteMode === 'Blank',
            validationSupport: this.setupValidationSupport()
        });
    }

    /**
     * Life cycle hook for when component is created
     */
    public async created() {

        let candidatures: ICandidate[] = [];

        // Trying to get prepared data first
        const preparedAdditionDataForCurrentElection = this.preparedAdditionalData?.find(x => x.electionId === this.election.electionId.id);

        if (preparedAdditionDataForCurrentElection
            && preparedAdditionDataForCurrentElection.candidates
            && preparedAdditionDataForCurrentElection.candidates.length > 0) {

            candidatures = preparedAdditionDataForCurrentElection.candidates;

        } else {

            candidatures = await this.apiClient.vote.listAllCandidatures(
                this.election.electionId.id
            );
        }

        this.setupCandidatures(candidatures);

        this.viewModel.isLoaded = true;

        if (this.election.autoSelectCandidatesNominatedByCommittee && this.hasCandidatesNominatedByCommittee) {

            this.toogleElectionCommitteeModeClickedOrPressed();
        }

        this.emitInitiated({
            electionId: ComplexIdHelper.createId(this.election.electionId),
            nbrOfCandidatures: this.totalNbrOfCandidates
        });
    }

    public async orderVote(orderedCandidate: ICandidateOrderingNewPositionEvent) {

        ArgumentValidator.notNullOrEmpty(orderedCandidate, 'orderedCandidate');

        const currentIndex = _.findIndex(this.selectedCandidatures, (x) => x.id === orderedCandidate.candidate.id);

        const newIndex = currentIndex + (orderedCandidate.direction === 'Forward' ? 1 : -1);

        // Removing current
        this.selectedCandidatures = _.reject(this.selectedCandidatures, (x) => x.id === orderedCandidate.candidate.id);

        // Re-adding
        this.selectedCandidatures.splice(newIndex, 0, orderedCandidate.candidate);

        this.emitVoteChanged({
            electionId: this.election.electionId,
            candidatesVerificationHash: this.viewModel.candidatesVerificationHash,
            candidatures: this.selectedCandidatures,
            validationSupport: this.setupValidationSupport()
        });
    }

    private setupValidationSupport(): IVoteValidationSupport {

        let allCandidatures: ICandidateViewModel[] = [];

        if (this.candidates.candidatesNominatedByCommittee && this.candidates.candidatesNominatedByCommittee.length > 0) {
            allCandidatures = allCandidatures.concat(...this.candidates.candidatesNominatedByCommittee);
        }
        if (this.candidates.otherCandidates && this.candidates.otherCandidates.length > 0) {
            allCandidatures = allCandidatures.concat(...this.candidates.otherCandidates);
        }

        return {
            electionGenderStatistics: _.countBy(allCandidatures, (x) => x && x.object && x.object.userData ? x.object.userData.gender : 'Undefined')
        };
    }

    private toggleSelection(candidate: ICandidateViewModel): void {

        if (candidate.object.disabledByNoSelfVoting) {

            return;
        }

        if (_.find(this.selectedCandidatures, x => x.id === candidate.object.id) === undefined) {

            this.selectedCandidatures.push(candidate.object);

        } else {

            this.ensureVoteRemoved(candidate, true);

        }

        this.emitVoteChanged({
            electionId: this.election.electionId,
            candidatesVerificationHash: this.viewModel.candidatesVerificationHash,
            candidatures: this.selectedCandidatures,
            validationSupport: this.setupValidationSupport()
        });
    }

    private ensureVoteRemoved(candidate: ICandidateViewModel, disableEventEmitter = false): void {

        ArgumentValidator.notNullOrEmpty(candidate, 'candidate');

        this.selectedCandidatures = _.reject(this.selectedCandidatures, x => x.id === candidate.object.id);

        if (disableEventEmitter) {

            return;
        }

        this.emitVoteChanged({
            electionId: this.election.electionId,
            candidatesVerificationHash: this.viewModel.candidatesVerificationHash,
            candidatures: this.selectedCandidatures,
            validationSupport: this.setupValidationSupport()
        });
    }

    private setupCandidatures(candidates: ICandidate[]): void {

        ConsoleFacade.log(`Got ${candidates.length} candidates, mapping...`);

        const showAllCandidatures = (this.election.candidatureDisplayType === 'Card');

        const hash = CandidatesVerificationHashCreator.computeHash(candidates);

        const mappedCandidates = CandidateMapper.toViewModel(
            candidates,
            showAllCandidatures,
            this.election.separateCandidatesNominatedByCommittee,
            this.election.numberCandidatesNominatedByCommittee,
            this.votingSettings.autoCandidateTypeaheadLimit
        );

        this.candidates.candidatesNominatedByCommittee = mappedCandidates.candidatesNominatedByCommittee;
        this.candidates.otherCandidates = mappedCandidates.otherCandidates;

        this.viewModel.candidatesVerificationHash = hash;

        this.emitVoteChanged({
            electionId: this.election.electionId,
            candidatesVerificationHash: this.viewModel.candidatesVerificationHash
        });

    }
}
